/**
 * Shared footer for all InterviewManager wizards (InterviewActivity + ScheduleInterview).
 * Renders: Back (optional) | Cancel + Secondary CTA (optional) + Primary
 */
import { useEffect, useRef, useState } from 'react';
import { Flex } from '@chakra-ui/react';
import { Button } from '@kula-ai/components';
import { ArrowLeft } from '@kula-ai/icons-core';

export interface WizardFooterProps {
  disabled: boolean;
  canGoBack: boolean;
  backButtonLabel?: string;
  primaryButtonLabel: string;
  cancelLabel?: string;
  /** Text shown on the in-flight CTA's loader (e.g. "Saving…"/"Scheduling…"). */
  loadingText?: string;
  onBack: () => void;
  onCancel: () => void;
  /** Primary / secondary handlers may return a promise — the footer awaits
   *  to know when the action settles for the re-entry guard. */
  onPrimary: () => void | Promise<void>;
  secondaryCta?: { label: string; onClick: () => void | Promise<void> };
}

export const WizardFooter = ({
  disabled,
  canGoBack,
  backButtonLabel,
  primaryButtonLabel,
  cancelLabel = 'Cancel',
  loadingText = 'Saving…',
  onBack,
  onCancel,
  onPrimary,
  secondaryCta,
}: WizardFooterProps) => {
  // Track which CTA the user invoked so its button shows a loader while the
  // action is in flight; the rest stay disabled (no spinner). The loader is
  // gated on `disabled` (the in-flight signal: validation + save), so the
  // marker self-clears the moment the action settles — no reset needed.
  // Back/Cancel don't hit the API, so they never get a loader.
  const [pendingCta, setPendingCta] = useState<'primary' | 'secondary' | null>(
    null
  );

  // Synchronous re-entry guard. `disabled` is React state — it flips true
  // a render-tick *after* the click handler runs, so two rapid clicks
  // (e.g. enter-pressed + button-clicked, or a fast double-click) can
  // both pass the `disabled` check on the same render and both invoke
  // the action. That fires duplicate validates + duplicate mutations.
  // QA-reproduced: edit mode `scheduleNow` doesn't flip `isBusy` at all,
  // so the window stays open through the entire validate phase. The
  // ref blocks within the same render tick; the effect releases it
  // once `disabled` has cycled true → false (action settled) — or the
  // `finally` releases it on fast-fail paths where `disabled` never
  // flipped because validation rejected synchronously.
  const inFlightRef = useRef(false);
  const sawDisabledTrueRef = useRef(false);
  useEffect(() => {
    if (disabled) {
      sawDisabledTrueRef.current = true;
      return;
    }
    if (sawDisabledTrueRef.current) {
      sawDisabledTrueRef.current = false;
      inFlightRef.current = false;
    }
  }, [disabled]);

  const runGuarded = async (
    cta: 'primary' | 'secondary',
    fn: () => void | Promise<void>
  ) => {
    if (inFlightRef.current || disabled) return;
    inFlightRef.current = true;
    setPendingCta(cta);
    try {
      await fn();
    } finally {
      // Fast-fail path (validate rejected synchronously, no mutation
      // fired): `disabled` never flipped, so the effect won't release
      // the lock. Clear it here so the next click is honored. The save
      // path takes the other branch — the effect handles release once
      // the mutation settles and `disabled` cycles back to false.
      if (!sawDisabledTrueRef.current) {
        inFlightRef.current = false;
      }
    }
  };

  const handlePrimary = () => {
    void runGuarded('primary', onPrimary);
  };
  const handleSecondary = () => {
    if (!secondaryCta) return;
    void runGuarded('secondary', secondaryCta.onClick);
  };

  return (
    <Flex justifyContent="space-between" align="center" gap="3">
      <Flex>
        {canGoBack && backButtonLabel && (
          <Button
            variant="quaternary"
            leftElement={<ArrowLeft size={16} />}
            disabled={disabled}
            onClick={onBack}
          >
            {backButtonLabel}
          </Button>
        )}
      </Flex>

      <Flex gap="3">
        <Button variant="secondary" disabled={disabled} onClick={onCancel}>
          {cancelLabel}
        </Button>
        {secondaryCta && (
          <Button
            variant="secondary"
            disabled={disabled}
            isLoading={disabled && pendingCta === 'secondary'}
            loadingText={loadingText}
            onClick={handleSecondary}
          >
            {secondaryCta.label}
          </Button>
        )}
        <Button
          variant="primary"
          disabled={disabled}
          isLoading={disabled && pendingCta === 'primary'}
          loadingText={loadingText}
          onClick={handlePrimary}
        >
          {primaryButtonLabel}
        </Button>
      </Flex>
    </Flex>
  );
};
